/**
 * Migrate Local Responses to Firestore
 *
 * Called once after a user registers (or logs in with existing anonymous data).
 * Reads responses from localStorage, writes them to Firestore using the
 * existing storage.ts functions, then clears localStorage.
 */

import {
  getAnonResponsesForMigration,
  hasAnonResponses,
  clearAnonData,
  getAnonInviteToken,
} from './local-storage-adapter';
import {
  saveResponse,
  toggleQuestionPrivacy,
  toggleSectionPrivacy,
  setComfortLevel,
  markOnboardingComplete,
  getOrCreateUserRelationship,
} from './storage';
import { joinRelationshipWithCode } from './storage';

export interface MigrationResult {
  success: boolean;
  responsesCount: number;
  errors: string[];
  relationshipId: string | null;
}

/**
 * Migrate all anonymous localStorage data to Firestore for a newly registered user.
 *
 * Steps:
 * 1. Get or create a relationship (auto-join if invite token present)
 * 2. Write all responses to Firestore
 * 3. Apply privacy settings
 * 4. Set comfort level and onboarding status
 * 5. Clear localStorage
 */
export async function migrateLocalResponsesToFirestore(
  userId: string,
  userEmail: string,
  userName?: string
): Promise<MigrationResult> {
  const result: MigrationResult = {
    success: false,
    responsesCount: 0,
    errors: [],
    relationshipId: null,
  };

  if (!hasAnonResponses()) {
    // Nothing to migrate — still successful
    result.success = true;
    return result;
  }

  const data = getAnonResponsesForMigration();

  try {
    // Step 1: Get or create relationship
    // If user arrived via invite link, try to join that relationship
    let relationshipId: string | null = null;

    if (data.inviteToken) {
      try {
        relationshipId = await joinRelationshipWithCode(
          userId,
          userEmail,
          data.inviteToken,
          userName
        );
        console.log('[Migration] Joined relationship via invite token:', relationshipId);
      } catch (joinError) {
        console.warn('[Migration] Failed to join via invite token, creating new relationship:', joinError);
        // Fall through to create a new relationship
      }
    }

    if (!relationshipId) {
      relationshipId = await getOrCreateUserRelationship(userId, userEmail);
    }

    if (!relationshipId) {
      result.errors.push('Failed to create or join a relationship');
      return result;
    }

    result.relationshipId = relationshipId;

    // Step 2: Set comfort level and mark onboarding complete
    try {
      await setComfortLevel(relationshipId, userId, data.comfortLevel);
      await markOnboardingComplete(relationshipId, userId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      result.errors.push(`Onboarding setup: ${msg}`);
      // Non-fatal — continue with responses
    }

    // Step 3: Write responses to Firestore
    const responseEntries = Object.entries(data.responses);
    let successCount = 0;

    for (const [questionId, response] of responseEntries) {
      try {
        await saveResponse(
          relationshipId,
          userId,
          questionId,
          response.value,
          response.isPrivate ?? false
        );
        successCount++;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        result.errors.push(`Response ${questionId}: ${msg}`);
      }
    }

    result.responsesCount = successCount;

    // Step 4: Apply section-level privacy
    for (const sectionId of data.privateSections) {
      try {
        await toggleSectionPrivacy(relationshipId, userId, sectionId, true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        result.errors.push(`Section privacy ${sectionId}: ${msg}`);
      }
    }

    // Step 5: Apply question-level privacy
    for (const questionId of data.privateQuestions) {
      try {
        await toggleQuestionPrivacy(relationshipId, userId, questionId, true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        result.errors.push(`Question privacy ${questionId}: ${msg}`);
      }
    }

    // Step 6: Clear localStorage only if we migrated all responses successfully
    if (result.errors.length === 0) {
      clearAnonData();
      console.log('[Migration] All data migrated successfully, localStorage cleared');
    } else {
      console.warn('[Migration] Partial migration, localStorage preserved for retry:', result.errors);
    }

    result.success = successCount > 0 || responseEntries.length === 0;
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    result.errors.push(`Fatal migration error: ${msg}`);
    console.error('[Migration] Fatal error:', error);
  }

  return result;
}
